(() => {
  if (window.__stbeScPersonalizationLabelsLoaded) return;

  window.__stbeScPersonalizationLabelsLoaded = true;

  let isSyncing = false;
  let observer;
  const checkedScCheckboxes = new Map();
  const pendingScCheckboxes = new Map();

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  function findPersonalizationSelects() {
    return [...document.querySelectorAll('select')].filter((select) => {
      const optionLabels = [...select.options].map((option) => normalize(option.textContent));

      return optionLabels.includes('name') && optionLabels.includes('monogram');
    });
  }

  function setOptionLabel(option, label) {
    if (!option) return;

    if (!option.dataset.stbeTriggerValue) {
      option.dataset.stbeTriggerValue = option.value;
    }

    if (option.dataset.stbeSubmitValue !== label) {
      option.dataset.stbeSubmitValue = label;
    }

    if (option.textContent.trim() !== label) {
      option.textContent = label;
    }

    if (option.value !== option.dataset.stbeTriggerValue) {
      option.value = option.dataset.stbeTriggerValue;
    }
  }

  function orderOptions(select, orderedOptions) {
    const currentOptions = [...select.options];
    const expectedOptions = orderedOptions.filter(Boolean);
    const isAlreadyOrdered = expectedOptions.every((option, index) => currentOptions[index] === option);

    if (isAlreadyOrdered) return;

    expectedOptions.forEach((option) => {
      select.appendChild(option);
    });
  }

  function syncPersonalizationLabels(select) {
    const optionsByKey = Object.fromEntries(
      [...select.options]
        .filter((option) => option.dataset.option_value_key)
        .map((option) => [option.dataset.option_value_key, option])
    );
    const monogramTrigger = optionsByKey['0'];
    const nameTrigger = optionsByKey['1'];
    const motifTrigger = optionsByKey['2'];

    if (!monogramTrigger || !nameTrigger) return;

    setOptionLabel(nameTrigger, 'Name');
    setOptionLabel(monogramTrigger, 'Monogram');

    const placeholder = [...select.options].find((option) => !option.dataset.option_value_key);
    orderOptions(select, [placeholder, nameTrigger, monogramTrigger, motifTrigger]);
  }

  function syncAllPersonalizationLabels() {
    if (isSyncing || document.activeElement?.matches?.('select')) return;

    isSyncing = true;
    findPersonalizationSelects().forEach(syncPersonalizationLabels);
    restoreScCheckboxes();
    isSyncing = false;
  }

  function getScCheckboxKey(input) {
    const option = input.closest('.bold_option_checkbox');
    const helper = option?.querySelector('input[type="hidden"][name^="properties["]');
    const title = option?.querySelector('.bold_option_title')?.textContent;

    return helper?.name || normalize(title);
  }

  function setScCheckboxState(input, checked) {
    const option = input.closest('.bold_option_checkbox');
    const helper = option?.querySelector('input[type="hidden"][name^="properties["]');

    input.checked = checked;
    input.defaultChecked = checked;
    input.indeterminate = false;
    input.setAttribute('aria-checked', checked ? 'true' : 'false');
    option?.classList.toggle('stbe-sc-checkbox-checked', checked);

    if (checked) {
      input.setAttribute('checked', 'checked');
    } else {
      input.removeAttribute('checked');
    }

    if (helper) {
      helper.value = checked ? helper.dataset.stbeCheckedValue || '✓' : '';
    }
  }

  function rememberScCheckbox(input, checked = input.checked) {
    if (!input?.matches?.('.bold_option_checkbox input[type="checkbox"]')) return;

    const key = getScCheckboxKey(input);
    if (!key) return;

    const helper = input
      .closest('.bold_option_checkbox')
      ?.querySelector('input[type="hidden"][name^="properties["]');

    if (helper?.value) {
      helper.dataset.stbeCheckedValue = helper.value;
      checked = true;
    }

    checkedScCheckboxes.set(key, checked);
    setScCheckboxState(input, checked);
  }

  function findScCheckboxByKey(key) {
    if (!key) return undefined;

    return [...document.querySelectorAll('.bold_option_checkbox input[type="checkbox"]')].find(
      (input) => getScCheckboxKey(input) === key
    );
  }

  function captureScCheckboxIntent(event) {
    const row = event.target.closest?.('.bold_option_checkbox');
    if (!row) return;

    const checkbox = row.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.disabled) return;
    if (event.target.closest('input, select, textarea, button, a')) return;

    const key = getScCheckboxKey(checkbox);
    if (!key) return;

    pendingScCheckboxes.set(key, {
      checked: !checkbox.checked,
      expiresAt: Date.now() + 1600,
    });
  }

  function reinforceScCheckboxIntent(event) {
    const row = event.target.closest?.('.bold_option_checkbox');
    if (!row) return;

    const checkbox = row.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.disabled) return;

    const key = getScCheckboxKey(checkbox);
    const pending = key ? pendingScCheckboxes.get(key) : undefined;
    const checked = pending && pending.expiresAt > Date.now() ? pending.checked : checkbox.checked;

    [0, 80, 240, 600, 1200].forEach((delay) => {
      window.setTimeout(() => {
        const currentCheckbox = findScCheckboxByKey(key) || checkbox;

        rememberScCheckbox(currentCheckbox, checked);
      }, delay);
    });
  }

  function restoreScCheckboxes() {
    document.querySelectorAll('.bold_option_checkbox input[type="checkbox"]').forEach((input) => {
      const key = getScCheckboxKey(input);
      if (!key) return;

      const helper = input
        .closest('.bold_option_checkbox')
        ?.querySelector('input[type="hidden"][name^="properties["]');

      if (helper?.value) {
        helper.dataset.stbeCheckedValue = helper.value;
        checkedScCheckboxes.set(key, true);
        setScCheckboxState(input, true);
        return;
      }

      const pending = pendingScCheckboxes.get(key);
      if (pending && pending.expiresAt > Date.now()) {
        setScCheckboxState(input, pending.checked);
        return;
      }

      if (pending) {
        pendingScCheckboxes.delete(key);
      }

      if (!checkedScCheckboxes.has(key)) return;

      setScCheckboxState(input, checkedScCheckboxes.get(key));
    });
  }

  function handleScCheckboxChange(event) {
    if (!event.target?.matches?.('.bold_option_checkbox input[type="checkbox"]')) return;

    if (event.isTrusted) {
      rememberScCheckbox(event.target);
      return;
    }

    const key = getScCheckboxKey(event.target);
    if (!key || !checkedScCheckboxes.has(key)) return;

    setScCheckboxState(event.target, checkedScCheckboxes.get(key));
  }

  function preparePersonalizationForSubmit() {
    findPersonalizationSelects().forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption?.dataset.stbeSubmitValue) return;

      selectedOption.value = selectedOption.dataset.stbeSubmitValue;

      window.setTimeout(() => {
        [...select.options].forEach((option) => {
          if (option.dataset.stbeTriggerValue) option.value = option.dataset.stbeTriggerValue;
        });
      }, 0);
    });
  }

  let syncTimer;
  function queueSync() {
    if (isSyncing) return;

    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncAllPersonalizationLabels, 80);
  }

  let scrollTimer;
  function queueScrollRestore() {
    window.clearTimeout(scrollTimer);
    window.requestAnimationFrame(restoreScCheckboxes);
    scrollTimer = window.setTimeout(restoreScCheckboxes, 120);
  }

  function init() {
    syncAllPersonalizationLabels();
    document.addEventListener('change', handleScCheckboxChange, true);
    document.addEventListener('pointerdown', captureScCheckboxIntent, true);
    document.addEventListener('click', reinforceScCheckboxIntent, true);
    document.addEventListener('scroll', queueScrollRestore, true);
    document.addEventListener('submit', preparePersonalizationForSubmit, true);
    document.addEventListener('click', (event) => {
      if (event.target.closest('button[type="submit"], input[type="submit"], add-to-cart-component')) {
        preparePersonalizationForSubmit();
      }
    }, true);

    observer = new MutationObserver(queueSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
